/**
 * StatusGlyph — severity as a shape, not only as a colour.
 *
 * The product codes its three severity tiers with distinct glyphs: a square
 * for the worst, a triangle for the middle, a circle for the mildest. Colour
 * carries the same information, but the shape survives a colour-blind reader,
 * a greyscale print and a screenshot pasted into a chat — all of which happen
 * to this report.
 *
 * The tone uses the `hover` step of each role rather than `main`: on a dark
 * canvas `main` is the fill colour for buttons and reads muddy at glyph size,
 * while `hover` is the same hue one step brighter.
 *
 * @format
 */

import {Icon} from 'ui/components/atoms/Icon';

import type {PaletteTone} from '../content';

export interface StatusGlyphProps {
  tone: PaletteTone;
  /** Glyph size in px. Default 22, matching the product's category headers. */
  size?: number;
}

/** Worst → mildest: filled square, triangle, hollow circle. */
const GLYPH: Record<PaletteTone, string> = {
  bad: 'square',
  alert: 'change_history',
  good: 'circle',
};

export function StatusGlyph({tone, size = 22}: StatusGlyphProps) {
  return (
    <Icon
      name={GLYPH[tone]}
      size={size}
      weight={500}
      // `Icon`'s named colours map to `main`; the brighter step comes from the
      // theme directly so the glyph holds up at this size on a dark surface.
      style={{color: `var(--ads-palette-${tone}-hover)`}}
      aria-hidden
    />
  );
}
